import { Injectable, OnDestroy, Provider, computed, inject, signal } from '@angular/core';
import { UserModel } from '@core/store/user/user.model';
import { AirportModel, HandlerInfoModel } from 'app/airport/models/airport.model';
import { AirportApiService } from 'app/airport/services/airport-api.service';
import { AirportBriefingPort } from 'app/airport/services/airport-briefing';
import { AirportBriefingHttpAdapter } from 'app/airport/services/airport-briefing-http.adapter';
import { AirportBriefingPdfPort } from 'app/airport/services/airport-briefing-pdf';
import { EMPTY, Subscription, catchError } from 'rxjs';
import { briefingHeroChips, buildBriefingSections } from './briefing-sections';
import {
  V2ActionState,
  V2BriefingActions,
  V2BriefingChip,
  V2BriefingSection,
  V2BriefingStatus,
  V2BriefingView,
} from './v2-briefing.model';

/**
 * The airport `airport-details.component.html` hardcodes in eight places.
 *
 * It is the product's sample briefing, not an accident: every gated section
 * opens for it and it offers a full PDF to viewers who otherwise meet a wall.
 * The number lives here once, and `isSampleBriefing` is the only thing that
 * reads it (data-gaps G5).
 */
export const SAMPLE_BRIEFING_AIRPORT_ID = 94;

export const V2_BRIEFING_LOAD_ERROR = 'This briefing did not load.';

/** Verbatim from the two disabled action blocks in the current template. */
export const V2_FEEDBACK_TOOLTIP_SIGNED_OUT = 'Available for signed in users only';
export const V2_FEEDBACK_TOOLTIP_NO_ACCESS = 'Your account does not have access to this feature';

function hiddenActions(): V2BriefingActions {
  return { pdf: 'hidden' as V2ActionState, feedback: 'hidden' as V2ActionState, feedbackTooltip: null };
}

/**
 * Owns one briefing's reads and handler choice through AirportBriefingPort.
 * Airport readiness is independent of the handler list: a slow or failed
 * handler read must not hide a readable briefing. Viewer preferences are
 * resolved when building the view, so neither response order nor a later
 * viewer update requires another request.
 *
 * Two things it deliberately does *not* do:
 *
 * - **No freshness stamp.** There is no per-section check date on the model
 *   and `updated_at` is a different, weaker claim (data-gaps G1). Deriving a
 *   tier from it would state a verification that never happened.
 * - **No parsing.** Every value reaches the template as it is stored.
 *
 * SSR-safe: no `window`, `document` or `localStorage`.
 */
@Injectable()
export class V2BriefingService implements OnDestroy {
  private readonly briefing = inject(AirportBriefingPort);

  private readonly _airport = signal<AirportModel | null>(null);
  private readonly _handlers = signal<HandlerInfoModel[]>([]);
  private readonly _handlerId = signal<number | null>(null);
  private readonly _status = signal<V2BriefingStatus>('idle');
  private readonly _error = signal<string | null>(null);

  /** Guards against a slow response for a tab the user has already left. */
  private requestToken = 0;
  private subscriptions = new Subscription();
  private lastRequestedId: number | string | null = null;

  readonly airport = this._airport.asReadonly();
  readonly status = this._status.asReadonly();
  readonly error = this._error.asReadonly();
  readonly loading = computed(() => this._status() === 'loading');
  readonly ready = computed(() => this._status() === 'ready');

  /**
   * Fetch one airport and its handlers independently, clearing any prior pick.
   *
   * The previous record is dropped first, on purpose: a hero left showing
   * LFPB's fields under LSGG's code is worse than a skeleton.
   *
   * Both an airport error and completion without a value mean load failure.
   * Handler errors leave the list empty without changing airport status.
   */
  load(id: number | string): void {
    this.cancel();

    this.lastRequestedId = id;
    this._airport.set(null);
    this._handlers.set([]);
    this._handlerId.set(null);
    this._error.set(null);
    this._status.set('loading');

    const token = ++this.requestToken;
    let received = false;

    this.subscriptions.add(this.briefing.getAirport(String(id)).subscribe({
      next: (airport: AirportModel) => {
        if (token !== this.requestToken) { return; }
        received = true;
        this._airport.set(airport ?? null);
        this._error.set(null);
        this._status.set('ready');
      },
      error: () => {
        if (token !== this.requestToken) { return; }
        this.fail();
      },
      complete: () => {
        if (token !== this.requestToken || received) { return; }
        this.fail();
      },
    }));

    this.subscriptions.add(this.briefing.getHandlerList(String(id)).pipe(
      catchError(() => EMPTY),
    ).subscribe(handlers => {
      if (token !== this.requestToken) { return; }
      this._handlers.set(Array.isArray(handlers) ? handlers : []);
      this._handlerId.set(null);
    }));
  }

  /** A picker choice lasts until a reload; null restores the viewer's default. */
  selectHandler(id: number | null): void {
    this._handlerId.set(id);
  }

  /**
   * Re-request whatever failed. A no-op before anything has been asked for,
   * so a retry control can be rendered without a guard around it.
   */
  retry(): void {
    if (this.lastRequestedId === null) { return; }
    this.load(this.lastRequestedId);
  }

  /** Back to the state before the first load -- closing the last tab. */
  reset(): void {
    this.cancel();
    this.requestToken++;
    this.lastRequestedId = null;
    this._airport.set(null);
    this._handlers.set([]);
    this._handlerId.set(null);
    this._error.set(null);
    this._status.set('idle');
  }

  /**
   * The one predicate that replaces the eight `airport().id === 94` branches.
   *
   * `Airport.is_demo` already existed on the Django model, driving
   * `UserProfile.has_briefing_permission` and the PDF queryset; it was simply
   * absent from `AirportSerializer.fields`, which is why the frontend had to
   * hardcode an id at all. It is now exposed, so the flag decides and moving
   * the demo is no longer a frontend release (data-gaps G5).
   *
   * The id fallback stays for one reason only: a cached or older payload that
   * predates the serializer change. It can come out once nothing serves that.
   */
  isSampleBriefing(airport: AirportModel | null | undefined): boolean {
    if (!airport) { return false; }

    if (typeof airport.is_demo === 'boolean') { return airport.is_demo; }

    return airport.id === SAMPLE_BRIEFING_AIRPORT_ID;
  }

  /**
   * `user() && showDetails() || airport().id === 94`, said once.
   *
   * `showDetails()` is `this.user().rights_briefing` and nothing else --
   * `is_staff` is deliberately not consulted, because widening the gate is a
   * product decision and not part of a re-skin.
   */
  canReadDetail(
    user: UserModel | null | undefined,
    airport: AirportModel | null | undefined,
  ): boolean {
    if (user?.rights_briefing) { return true; }
    return this.isSampleBriefing(airport);
  }

  /**
   * The hero actions, folding the old template's three `airport-actions`
   * blocks into one answer. The signed-out branch cannot be reached this
   * pass -- every route is inside `AuthGuard` (data-gaps G12) -- but it is
   * reproduced rather than dropped, so nothing has to be rediscovered when
   * public briefings arrive.
   */
  actions(
    user: UserModel | null | undefined,
    airport: AirportModel | null | undefined,
  ): V2BriefingActions {
    if (!airport) { return hiddenActions(); }

    if (user?.rights_briefing) {
      return { pdf: 'enabled', feedback: 'enabled', feedbackTooltip: null };
    }

    if (this.isSampleBriefing(airport)) {
      return {
        pdf: 'enabled',
        feedback: 'disabled',
        feedbackTooltip: user ? V2_FEEDBACK_TOOLTIP_NO_ACCESS : V2_FEEDBACK_TOOLTIP_SIGNED_OUT,
      };
    }

    return hiddenActions();
  }

  /**
   * All six sections for the loaded airport and this viewer.
   *
   * Ops & handling reads the same selected handler as the picker and PDF.
   */
  sections(
    user: UserModel | null | undefined,
  ): readonly V2BriefingSection[] {
    const airport = this._airport();

    return buildBriefingSections({
      airport,
      handler: this.handlerFor(user),
      canReadDetail: this.canReadDetail(user, airport),
    });
  }

  /** The one hero chip the data supports (data-gaps G3). */
  chips(): readonly V2BriefingChip[] {
    return briefingHeroChips(this._airport());
  }

  /** Everything a briefing page needs, for one viewer, in one object. */
  view(
    user: UserModel | null | undefined,
  ): V2BriefingView {
    const airport = this._airport();

    return {
      airport,
      user: user ?? null,
      handlers: this._handlers(),
      handler: this.handlerFor(user),
      canReadDetail: this.canReadDetail(user, airport),
      isSample: this.isSampleBriefing(airport),
      chips: this.chips(),
      sections: this.sections(user),
      actions: this.actions(user, airport),
    };
  }

  ngOnDestroy(): void {
    this.cancel();
  }

  /** Listed viewer preference, then listed airport preference, then first. */
  private handlerFor(user: UserModel | null | undefined): HandlerInfoModel | null {
    const list = this._handlers();
    if (!list.length) { return null; }
    const pickedId = this._handlerId();
    if (pickedId !== null) {
      return list.find(handler => handler.id === pickedId) ?? null;
    }

    const airport = this._airport();
    const userDefault = (user?.user_default_handlers ?? [])
      .find(item => item?.airport_data?.id === airport?.id)?.handler_data?.id;
    const preferred = airport?.handler_info as HandlerInfoModel | number | null | undefined;
    const preferredId = typeof preferred === 'number' ? preferred : preferred?.id;
    const listed = (id: number | null | undefined) =>
      id != null ? list.find(handler => handler.id === id) : undefined;

    return listed(userDefault) ?? listed(preferredId) ?? list[0] ?? null;
  }

  private fail(): void {
    this._airport.set(null);
    this._error.set(V2_BRIEFING_LOAD_ERROR);
    this._status.set('error');
  }

  private cancel(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
  }
}

/**
 * What a v2 briefing route has to provide.
 *
 * `AirportApiService` is `@Injectable()` without `providedIn`, so a v2 route
 * registers it here rather than leaning on `AirportModule`. Nothing in
 * `app/airport/` is modified to make this work.
 *
 * Rewired in the develop merge (2026-09-07). This used to register
 * `AirportStore` and `AirportStoreService` too, because `AirportApiService`
 * depended on them; develop's `d2e94c4` deleted that store and moved airport
 * and handler loading behind `AirportBriefingPort`.
 *
 * The PORT, not `AirportBriefingLoader`. The loader is the old briefing's
 * whole operation -- it sets the page title and raises its own toasts on
 * failure -- and this service already owns a status signal, a load error
 * string and a request token for exactly that. Two error surfaces for one
 * failed request is a worse briefing, not a safer one.
 */
export const V2_BRIEFING_PROVIDERS: readonly Provider[] = [
  AirportApiService,
  { provide: AirportBriefingPdfPort, useExisting: AirportApiService },
  AirportBriefingHttpAdapter,
  { provide: AirportBriefingPort, useExisting: AirportBriefingHttpAdapter },
  V2BriefingService,
];
